/**
 * AdminBulkInvite — CSV-in / CSV-out batch onboarding for Reclaim.
 *
 * Route: `/admin/bulk-invite` (admin-only).
 *
 * Flow:
 *   1. Paste CSV in a textarea (or drop a .csv file)
 *      Required columns: name
 *      Optional:        email,password,atelier_credits,context,mint_welcome
 *   2. Preview parsed rows (tabular, editable per cell — soon)
 *   3. Hit "Invite N members" — creates accounts + grants credits +
 *      optionally kicks off concierge welcome renders in background
 *   4. Download the result CSV (mailmerge-ready: name,email,password,login_url)
 *
 * Auto-generated fields when the CSV omits them:
 *   · email    → <slug(name)>@reclaim.ai
 *   · password → memorable 3-word + 4-digit combo
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { AdminLayout } from "../AdminPages";
import {
  ArrowLeft, Upload, Users, Loader2, Check, AlertTriangle, X, Download,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("reclaim_token")}` });

const SAMPLE_CSV = `name,email,atelier_credits,context,mint_welcome
Ayten,ayten@reclaim.ai,2,Long-time Reclaim family · needs Inner Path resets,true
Annie,,1,,false
Ra,,1,,false`;

// Minimal CSV parser — supports quoted fields, escaped quotes.
function parseCSV(text) {
  const rows = [];
  let cur = [], field = "", inQuotes = false, i = 0;
  while (i < text.length) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"' && text[i + 1] === '"') { field += '"'; i += 2; continue; }
      if (c === '"') { inQuotes = false; i++; continue; }
      field += c; i++; continue;
    }
    if (c === '"') { inQuotes = true; i++; continue; }
    if (c === ",") { cur.push(field); field = ""; i++; continue; }
    if (c === "\n" || c === "\r") {
      if (c === "\r" && text[i + 1] === "\n") i++;
      cur.push(field); rows.push(cur); cur = []; field = ""; i++; continue;
    }
    field += c; i++;
  }
  if (field.length || cur.length) { cur.push(field); rows.push(cur); }
  return rows.filter((r) => r.some((cell) => cell.trim() !== ""));
}

function rowsToInvitees(rows) {
  if (rows.length < 2) return [];
  const header = rows[0].map((h) => h.trim().toLowerCase());
  const idx = (col) => header.indexOf(col);
  const nameIdx = idx("name");
  if (nameIdx < 0) throw new Error("CSV must have a 'name' column");
  return rows.slice(1).map((r) => ({
    name: r[nameIdx]?.trim(),
    email: idx("email") >= 0 ? r[idx("email")]?.trim() || null : null,
    password: idx("password") >= 0 ? r[idx("password")]?.trim() || null : null,
    atelier_credits: idx("atelier_credits") >= 0 ? parseInt(r[idx("atelier_credits")], 10) || 0 : 0,
    context: idx("context") >= 0 ? r[idx("context")]?.trim() || null : null,
    mint_welcome: idx("mint_welcome") >= 0
      ? ["true", "1", "yes", "y"].includes((r[idx("mint_welcome")] || "").toLowerCase().trim())
      : false,
  })).filter((row) => row.name);
}

export default function AdminBulkInvite() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [csvText, setCsvText] = useState(SAMPLE_CSV);
  const [invitees, setInvitees] = useState([]);
  const [parseErr, setParseErr] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState(null);
  const [flash, setFlash] = useState(null);

  const preview = () => {
    setParseErr(null); setInvitees([]);
    try {
      const rows = parseCSV(csvText);
      const parsed = rowsToInvitees(rows);
      if (parsed.length === 0) throw new Error("No valid rows found");
      setInvitees(parsed);
    } catch (e) { setParseErr(e.message); }
  };

  const submit = async () => {
    if (invitees.length === 0) return;
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/api/admin/bulk-invite`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...auth() },
        body: JSON.stringify({ invitees }),
      });
      const j = await r.json();
      if (!r.ok) {
        setFlash({ kind: "err", text: j.detail || "Bulk invite failed" });
      } else {
        setResults(j);
        setFlash({ kind: "ok", text: `Done · ${j.created_count} created · ${j.already_existed} already existed` });
      }
    } catch (_) {
      setFlash({ kind: "err", text: "Network error" });
    }
    setSubmitting(false);
  };

  // Download result as mailmerge-ready CSV
  const downloadCSV = () => {
    if (!results) return;
    const header = ["name", "email", "password", "login_url", "atelier_credits", "membership_tier", "welcome_job_id"];
    const escape = (v) => {
      const s = String(v ?? "");
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [header.join(",")].concat(
      results.invitees.map((r) => header.map((h) => escape(r[h] || "")).join(","))
    );
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `reclaim-invites-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const handleFileDrop = async (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files[0];
    if (f) setCsvText(await f.text());
  };

  if (user?.role !== "admin") {
    return (
      <AdminLayout>
        <div className="text-center py-12">
          <AlertTriangle size={32} className="mx-auto text-amber-500 mb-3" />
          <p className="text-[#78716C]">Admin access required.</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-bulk-invite">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-heading text-xl text-[#292524] flex items-center gap-2">
              <Users size={18} className="text-amber-500" />
              Bulk Invite via CSV
            </h2>
            <p className="text-sm text-[#78716C]">
              Onboard multiple members in one shot. Auto-generates emails + passwords when missing.
              Optionally mints Faye-style concierge welcomes in the background.
            </p>
          </div>
          <button onClick={() => navigate("/admin")} className="flex items-center gap-2 text-sm text-[#78716C] hover:text-[#292524]" data-testid="bulk-invite-back">
            <ArrowLeft size={16} /> Back
          </button>
        </div>

        {flash && (
          <div className={`rounded-xl px-4 py-3 text-sm flex items-center justify-between ${
              flash.kind === "ok" ? "bg-emerald-50 text-emerald-800 border border-emerald-200"
                                  : "bg-red-50 text-red-800 border border-red-200"
            }`} data-testid="bulk-invite-flash">
            <div className="flex items-center gap-2">
              {flash.kind === "ok" ? <Check size={14} /> : <AlertTriangle size={14} />}
              <span>{flash.text}</span>
            </div>
            <button onClick={() => setFlash(null)}><X size={14} /></button>
          </div>
        )}

        {/* Step 1 · paste CSV */}
        <div className="rounded-2xl border border-[#E6E2DD] bg-white px-5 py-4 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-medium text-[#292524]">Paste CSV or drop a file</h3>
            <p className="text-[10px] text-[#78716C] uppercase tracking-wider">
              columns: <span className="font-mono">name, email?, atelier_credits?, context?, mint_welcome?</span>
            </p>
          </div>
          <textarea
            value={csvText}
            onChange={(e) => setCsvText(e.target.value)}
            onDrop={handleFileDrop}
            onDragOver={(e) => e.preventDefault()}
            rows={9}
            className="w-full font-mono text-xs px-3 py-2 rounded-lg border border-[#D6D3D1] bg-[#FAFAF9] focus:outline-none focus:ring-2 focus:ring-amber-400"
            data-testid="bulk-invite-csv-input"
            placeholder="Drop a .csv file here or paste rows above"
          />
          <div className="flex items-center gap-2">
            <button onClick={preview} className="rounded-full bg-[#292524] hover:bg-black text-white text-xs px-3 py-2 flex items-center gap-1.5" data-testid="bulk-invite-preview-btn">
              <Upload size={12} /> Parse CSV
            </button>
            {parseErr && <span className="text-xs text-red-600">{parseErr}</span>}
          </div>
        </div>

        {/* Step 2 · preview */}
        {invitees.length > 0 && !results && (
          <div className="rounded-2xl border border-[#E6E2DD] bg-white px-5 py-4 space-y-3" data-testid="bulk-invite-preview">
            <h3 className="font-medium text-[#292524]">{invitees.length} invitees ready</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-[#78716C] uppercase tracking-wider">
                    <th className="px-2 py-1.5">Name</th>
                    <th className="px-2 py-1.5">Email</th>
                    <th className="px-2 py-1.5">Ateliers</th>
                    <th className="px-2 py-1.5">Welcome</th>
                    <th className="px-2 py-1.5">Context</th>
                  </tr>
                </thead>
                <tbody>
                  {invitees.map((i, idx) => (
                    <tr key={idx} className="border-t border-[#E6E2DD]">
                      <td className="px-2 py-1.5 font-medium text-[#292524]">{i.name}</td>
                      <td className="px-2 py-1.5 text-[#78716C]">{i.email || <em className="text-[#A8A29E]">auto</em>}</td>
                      <td className="px-2 py-1.5">{i.atelier_credits}</td>
                      <td className="px-2 py-1.5">{i.mint_welcome ? "✓" : ""}</td>
                      <td className="px-2 py-1.5 text-[#78716C] max-w-[300px] truncate">{i.context || ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button onClick={submit} disabled={submitting} className="rounded-full bg-amber-500 hover:bg-amber-600 text-white text-sm px-4 py-2 flex items-center gap-2 disabled:opacity-40" data-testid="bulk-invite-submit-btn">
              {submitting ? <Loader2 size={14} className="animate-spin" /> : <Users size={14} />}
              Invite {invitees.length} member{invitees.length === 1 ? "" : "s"}
            </button>
          </div>
        )}

        {/* Step 3 · results */}
        {results && (
          <div className="rounded-2xl border border-emerald-300 bg-emerald-50/30 px-5 py-4 space-y-3" data-testid="bulk-invite-results">
            <div className="flex items-center justify-between">
              <h3 className="font-medium text-[#292524]">
                Done · {results.created_count} created · {results.already_existed} already existed
              </h3>
              <button onClick={downloadCSV} className="rounded-full bg-[#292524] hover:bg-black text-white text-xs px-3 py-2 flex items-center gap-1.5" data-testid="bulk-invite-download-csv">
                <Download size={12} /> Mailmerge CSV
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-[#78716C] uppercase tracking-wider">
                    <th className="px-2 py-1.5">Name</th>
                    <th className="px-2 py-1.5">Email</th>
                    <th className="px-2 py-1.5">Password</th>
                    <th className="px-2 py-1.5">Ateliers</th>
                    <th className="px-2 py-1.5">Welcome job</th>
                  </tr>
                </thead>
                <tbody>
                  {results.invitees.map((r, idx) => (
                    <tr key={idx} className="border-t border-emerald-200">
                      <td className="px-2 py-1.5 font-medium text-[#292524]">{r.name}</td>
                      <td className="px-2 py-1.5 text-[#78716C]">{r.email}</td>
                      <td className="px-2 py-1.5 font-mono text-[10px]">
                        {r.password || <em className="text-[#A8A29E]">unchanged (existed)</em>}
                      </td>
                      <td className="px-2 py-1.5">{r.atelier_credits}</td>
                      <td className="px-2 py-1.5">
                        {r.welcome_job_id
                          ? <span className="text-emerald-700 font-mono">{r.welcome_job_id}</span>
                          : <em className="text-[#A8A29E]">—</em>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-emerald-800">
              Concierge welcomes render in the background (~90s each). Poll status via
              <span className="font-mono"> /api/admin/bulk-invite/welcome-status/&lt;job_id&gt;</span>.
            </p>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
